import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { fetchMyJobs } from '../services/productionPlanService';

const getToken = () => localStorage.getItem('token');

const MobileMyJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState(0); // 0 = today, 1 = week, 2 = all

  const loadJobs = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = getToken();
      
      if (!token) {
        console.error('[MyJobs] No token found');
        setError('Ikke innlogget');
        setLoading(false);
        return;
      }
      
      let startDate = null;
      let endDate = null;

      if (viewMode === 0) {
        // Selected day - use local date to avoid timezone issues
        const selectedDay = new Date(selectedDate);
        const year = selectedDay.getFullYear();
        const month = String(selectedDay.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDay.getDate()).padStart(2, '0');
        
        startDate = `${year}-${month}-${day}`;
        
        // Next day
        const nextDay = new Date(selectedDay);
        nextDay.setDate(nextDay.getDate() + 1);
        const nextYear = nextDay.getFullYear();
        const nextMonth = String(nextDay.getMonth() + 1).padStart(2, '0');
        const nextDayNum = String(nextDay.getDate()).padStart(2, '0');
        
        endDate = `${nextYear}-${nextMonth}-${nextDayNum}`;
      } else if (viewMode === 1) {
        // This week
        const startOfWeek = new Date(selectedDate);
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1); // Monday
        startOfWeek.setHours(0, 0, 0, 0);
        
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(endOfWeek.getDate() + 7);
        
        startDate = startOfWeek.toISOString().split('T')[0];
        endDate = endOfWeek.toISOString().split('T')[0];
      }
      // viewMode === 2: no date filter (all jobs)

      console.log('[MyJobs] Fetching jobs with dates:', { startDate, endDate });
      const data = await fetchMyJobs(token, startDate, endDate);
      console.log('[MyJobs] Received jobs:', data);
      setJobs(data || []);
    } catch (err) {
      console.error('[MyJobs] Error loading jobs:', err);
      setError(err.message || 'Kunne ikke laste jobber');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, viewMode]);

  const handlePreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('nb-NO', { 
      weekday: 'short',
      day: '2-digit', 
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('nb-NO', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const calculateDuration = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const hours = (endDate - startDate) / (1000 * 60 * 60);
    return hours.toFixed(1);
  };

  const groupJobsByDate = (jobsList) => {
    const grouped = {};
    jobsList.forEach(job => {
      const dateKey = new Date(job.startTime).toISOString().split('T')[0];
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(job);
    });
    return grouped;
  };

  const renderJobCard = (job) => (
    <Card 
      key={job.id}
      sx={{ 
        mb: 2,
        borderLeft: job.completed ? '4px solid #4caf50' : '4px solid #2196f3'
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="div" fontWeight="bold" sx={{ fontSize: '1rem' }}>
            {job.memo || 'Produksjonsjobb'}
          </Typography>
          {job.completed && (
            <Chip 
              icon={<CheckCircleIcon />}
              label="Ferdig" 
              color="success" 
              size="small"
            />
          )}
        </Box>

        {job.orderNumber && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <AssignmentIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Ordre: {job.orderNumber}
            </Typography>
          </Box>
        )}

        {job.customer && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <BusinessIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {job.customer}
            </Typography>
          </Box>
        )}

        {job.teamName && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Team: {job.teamName}
            </Typography>
          </Box>
        )}

        {job.workOrderDescription && (
          <Box sx={{ 
            backgroundColor: '#e3f2fd',
            padding: 1.5,
            borderRadius: 1,
            mt: 1,
            mb: 1
          }}>
            <Typography variant="body2" fontWeight="500" color="primary.main" sx={{ mb: 0.5 }}>
              Beskrivelse:
            </Typography>
            <Typography variant="body2" color="text.primary">
              {job.workOrderDescription}
            </Typography>
          </Box>
        )}

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          backgroundColor: '#f5f5f5',
          padding: 1,
          borderRadius: 1,
          mt: 1
        }}>
          <ScheduleIcon sx={{ fontSize: 18, mr: 1, color: 'primary.main' }} />
          <Typography variant="body2" fontWeight="600">
            {formatTime(job.startTime)} - {formatTime(job.endTime)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {calculateDuration(job.startTime, job.endTime)}t
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    if (jobs.length === 0) {
      return (
        <Box sx={{ 
          textAlign: 'center', 
          py: 6,
          px: 2
        }}>
          <ScheduleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Ingen planlagte jobber
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {viewMode === 0 && 'Du har ingen jobber planlagt for denne dagen'}
            {viewMode === 1 && 'Du har ingen jobber planlagt denne uken'}
            {viewMode === 2 && 'Du har ingen planlagte jobber'}
          </Typography>
        </Box>
      );
    }

    if (viewMode === 0 || viewMode === 1) {
      // Group by date for day/week view
      const groupedJobs = groupJobsByDate(jobs);
      const sortedDates = Object.keys(groupedJobs).sort();

      return sortedDates.map(dateKey => (
        <Box key={dateKey} sx={{ mb: 3 }}>
          <Typography 
            variant="subtitle1" 
            fontWeight="bold" 
            sx={{ 
              mb: 1.5,
              pb: 0.5,
              borderBottom: '2px solid',
              borderColor: 'primary.main'
            }}
          >
            {formatDate(dateKey)}
          </Typography>
          {groupedJobs[dateKey].map(renderJobCard)}
        </Box>
      ));
    }

    // All jobs view
    return jobs.map(renderJobCard);
  };

  const selectedDateStr = selectedDate.toLocaleDateString('nb-NO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        Mine Jobber
      </Typography>

      <Tabs 
        value={viewMode} 
        onChange={(e, newValue) => setViewMode(newValue)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="I dag" />
        <Tab label="Denne uken" />
        <Tab label="Alle" />
      </Tabs>

      {viewMode === 0 && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 2,
          backgroundColor: '#f5f5f5',
          padding: 1.5,
          borderRadius: 1
        }}>
          <IconButton onClick={handlePreviousDay} size="small">
            <ChevronLeftIcon />
          </IconButton>
          
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="body1" fontWeight="600">
              {selectedDateStr}
            </Typography>
            <Typography 
              variant="caption" 
              color="primary" 
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={handleToday}
            >
              Gå til i dag
            </Typography>
          </Box>
          
          <IconButton onClick={handleNextDay} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}

      {renderContent()}
    </Box>
  );
};

export default MobileMyJobs;
